import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { protectedApi } from '../../services/api';

// Async thunks
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async ({ category, search, skip = 0, limit = 12 } = {}) => {
    try {
      console.log('Calling API with params:', { category, search, skip, limit });
      const response = await protectedApi.getProducts({ category, search, skip, limit });
      console.log('API Response:', response);
      
      // If response.data is an array, return it directly
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      // If response.data has a nested data property (common in some API structures)
      if (response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      
      // If we get here, something unexpected happened
      console.error('Unexpected API response structure:', response);
      return [];
    } catch (error) {
      console.error('Fetch products error:', error);
      throw error.response?.data?.detail || error.message || 'Failed to fetch products';
    }
  }
);

export const fetchProductBySlug = createAsyncThunk(
  'products/fetchProductBySlug',
  async (slug) => {
    try {
      const response = await protectedApi.getProductBySlug(slug);
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to fetch product';
    }
  }
);

// Protected operations (require authentication)
export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (productData) => {
    try {
      const response = await protectedApi.createProduct(productData);
      return response.data;
    } catch (error) {
      console.error('Create product error:', error);
      throw error.response?.data?.detail || error.message || 'Failed to create product';
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, productData }) => {
    try {
      const response = await protectedApi.updateProduct(id, productData);
      return response.data;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to update product';
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (id) => {
    try {
      await protectedApi.deleteProduct(id);
      return id;
    } catch (error) {
      throw error.response?.data?.detail || 'Failed to delete product';
    }
  }
);

const initialState = {
  items: [],
  currentProduct: null,
  status: 'idle',
  error: null,
  totalCount: 0
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Fetch Single Product
      .addCase(fetchProductBySlug.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchProductBySlug.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentProduct = action.payload;
        state.error = null;
      })
      .addCase(fetchProductBySlug.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Create Product
      .addCase(createProduct.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items.push(action.payload);
        state.error = null;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Update Product
      .addCase(updateProduct.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Delete Product
      .addCase(deleteProduct.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = state.items.filter(item => item.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  }
});

export const { clearCurrentProduct, clearError } = productsSlice.actions;
export default productsSlice.reducer;
